import type { UpdateProfileDto } from '@/apis/backend/generated/models';

import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/store/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import { useFormatters } from '@/hooks/use-formatters';
import { useLocale } from '@/providers/locale.provider';
import { type SupportedLanguage } from '@/locales/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { useAuthControllerUpdateProfile } from '@/apis/backend/generated/auth/auth';
import { NotificationPreferencesCard } from '@/sections/notifications/notification-preferences-card';
import {
  useInvitationsControllerAccept,
  useInvitationsControllerDecline,
  useInvitationsControllerFindMine,
  getInvitationsControllerFindMineQueryKey,
} from '@/apis/backend/generated/invitations/invitations';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import List from '@mui/material/List';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText';
import {
  Key as KeyIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
  ContentCopy as ContentCopyIcon,
  MailOutline as MailOutlineIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/profile/',
)({
  component: AccountSettingsPage,
});

// Dev-only language switcher options. Gated behind import.meta.env.DEV until
// the UI is translated (see docs/i18n/spanish-localization-plan.md).
const LANGUAGE_OPTIONS: { value: SupportedLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
];

function AccountSettingsPage() {
  const { t } = useTranslation(['settings', 'common']);
  const { formatDate } = useFormatters();
  const { dbUser, refresh, logout } = useAuth();
  const { language, setLanguage } = useLocale();
  const updateProfileMutation = useAuthControllerUpdateProfile();
  const queryClient = useQueryClient();
  const [processingToken, setProcessingToken] = useState<string | null>(null);

  const { data: pendingInvitations, isLoading: loadingInvitations } =
    useInvitationsControllerFindMine();

  const acceptInvitationMutation = useInvitationsControllerAccept();
  const declineInvitationMutation = useInvitationsControllerDecline();

  const invalidateInvitations = () =>
    queryClient.invalidateQueries({ queryKey: getInvitationsControllerFindMineQueryKey() });

  const handleAcceptInvitation = async (token: string, orgName: string) => {
    setProcessingToken(token);
    try {
      await acceptInvitationMutation.mutateAsync({ token });
      toast.success(t('profile.invitations.joined', { org: orgName }));
      await refresh();
      invalidateInvitations();
    } catch {
      toast.error(t('profile.invitations.acceptError'));
    } finally {
      setProcessingToken(null);
    }
  };

  const handleDeclineInvitation = async (token: string) => {
    setProcessingToken(token);
    try {
      await declineInvitationMutation.mutateAsync({ token });
      toast.success(t('profile.invitations.declined'));
      invalidateInvitations();
    } catch {
      toast.error(t('profile.invitations.declineError'));
    } finally {
      setProcessingToken(null);
    }
  };

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [dialogOpen, setDialogOpen] = useState<'password' | 'sessions' | 'delete' | null>(null);

  useEffect(() => {
    if (dbUser) {
      setFirstName(dbUser.firstName || '');
      setLastName(dbUser.lastName || '');
    }
  }, [dbUser]);

  // Arriving from an email footer's "manage preferences" link. The browser's own
  // anchor scroll runs before this page has mounted, so the hash has to be
  // re-applied once there is something to scroll to.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (dbUser) {
      const firstNameChanged = firstName !== (dbUser.firstName || '');
      const lastNameChanged = lastName !== (dbUser.lastName || '');
      setHasChanges(firstNameChanged || lastNameChanged);
    }
  }, [firstName, lastName, dbUser]);

  const handleCopyId = async () => {
    if (dbUser?.id) {
      await navigator.clipboard.writeText(dbUser.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    const updates: UpdateProfileDto = {};
    if (firstName !== (dbUser?.firstName || '')) updates.firstName = firstName;
    if (lastName !== (dbUser?.lastName || '')) updates.lastName = lastName;

    updateProfileMutation.mutate(
      { data: updates },
      {
        onSuccess: async () => {
          await refresh();
          setSaveSuccess(true);
          setHasChanges(false);
          setTimeout(() => setSaveSuccess(false), 3000);
        },
      }
    );
  };

  const handleReset = () => {
    if (dbUser) {
      setFirstName(dbUser.firstName || '');
      setLastName(dbUser.lastName || '');
    }
  };

  const handleChangePassword = () => setDialogOpen('password');
  const handleManageSessions = () => setDialogOpen('sessions');
  const handleDeleteAccount = () => setDialogOpen('delete');
  const closeDialog = () => setDialogOpen(null);

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {t('profile.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('profile.subtitle')}
        </Typography>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('profile.updateSuccess')}
        </Alert>
      )}

      {updateProfileMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('profile.updateError')}
        </Alert>
      )}

      {/* Personal Information */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={t('profile.personalInfo.title')}
          subheader={t('profile.personalInfo.subtitle')}
          action={
            hasChanges ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleReset}
                  disabled={updateProfileMutation.isPending}
                >
                  {t('common:reset')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 0.5 }} color="inherit" />
                      {t('common:saving')}
                    </>
                  ) : (
                    t('common:save')
                  )}
                </Button>
              </Box>
            ) : null
          }
        />
        <Divider />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('profile.personalInfo.firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 255 }}
              />
              <TextField
                label={t('profile.personalInfo.lastName')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 255 }}
              />
            </Box>

            <TextField
              label={t('profile.personalInfo.email')}
              value={dbUser?.userName || ''}
              fullWidth
              disabled
              helperText={t('profile.personalInfo.emailHelp')}
              InputProps={{ readOnly: true }}
            />

            <TextField
              label={t('profile.personalInfo.userId')}
              value={dbUser?.id || ''}
              fullWidth
              disabled
              helperText={t('profile.personalInfo.userIdHelp')}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copiedId ? t('common:copied') : t('common:copyToClipboard')}>
                      <IconButton onClick={handleCopyId} edge="end" size="small" disabled={!dbUser?.id}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Language (dev only until the UI is translated) */}
      {import.meta.env.DEV && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('profile.language.title')}
            subheader={t('profile.language.subtitle')}
          />
          <Divider />
          <CardContent>
            <TextField
              select
              label={t('profile.language.label')}
              value={language}
              onChange={(e) => setLanguage(e.target.value as SupportedLanguage)}
              sx={{ maxWidth: 320 }}
              fullWidth
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations */}
      {(loadingInvitations || (pendingInvitations && pendingInvitations.length > 0)) && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={t('profile.invitations.title')}
            subheader={t('profile.invitations.subtitle')}
          />
          <Divider />
          <CardContent>
            {loadingInvitations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {pendingInvitations?.map((invitation) => (
                  <Box
                    key={invitation.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      <MailOutlineIcon color="action" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {invitation.organizationName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('profile.invitations.invitedBy', { name: invitation.inviterName })} ·{' '}
                          {new Date(invitation.expiresAt) > new Date()
                            ? t('profile.invitations.expires', {
                                date: formatDate(invitation.expiresAt),
                              })
                            : t('profile.invitations.expired')}
                        </Typography>
                      </Box>
                      <Chip
                        label={invitation.role}
                        size="small"
                        color={invitation.role === 'owner' ? 'primary' : 'default'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() =>
                          handleAcceptInvitation(invitation.token, invitation.organizationName)
                        }
                        disabled={processingToken === invitation.token}
                      >
                        {processingToken === invitation.token && acceptInvitationMutation.isPending
                          ? t('profile.invitations.accepting')
                          : t('common:accept')}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => handleDeclineInvitation(invitation.token)}
                        disabled={processingToken === invitation.token}
                      >
                        {t('common:decline')}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notifications. The settings live here with everything else a person
          decides about themselves; the inbox they apply to is its own page, one
          click away through the card's own action. */}
      {/* The id is the anchor notification emails link to: "manage preferences"
          in a footer has to land on the setting itself, not on the top of a long
          settings page the reader then has to scan. */}
      <Box id="notification-preferences" sx={{ mb: 3, scrollMarginTop: 96 }}>
        <NotificationPreferencesCard />
      </Box>

      {/* Security */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title={t('profile.security.title')} subheader={t('profile.security.subtitle')} />
        <Divider />
        <CardContent>
          <List>
            <ListItem
              sx={{ pr: 14 }}
              secondaryAction={
                <Button variant="outlined" size="small" onClick={handleChangePassword}>
                  {t('common:change')}
                </Button>
              }
            >
              <ListItemIcon>
                <KeyIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('profile.security.password')}
                secondary={t('profile.security.passwordChanged')}
              />
            </ListItem>

            <ListItem
              sx={{ pr: 16 }}
              secondaryAction={
                <Button variant="outlined" size="small" disabled>
                  {t('common:configure')}
                </Button>
              }
            >
              <ListItemIcon>
                <SecurityIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('profile.security.twoFactor')}
                secondary={t('profile.security.twoFactorHelp')}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Sessions */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title={t('profile.sessions.title')} subheader={t('profile.sessions.subtitle')} />
        <Divider />
        <CardContent>
          <List>
            <ListItem
              sx={{ pr: 14 }}
              secondaryAction={
                <Button variant="outlined" size="small" onClick={handleManageSessions}>
                  {t('common:manage')}
                </Button>
              }
            >
              <ListItemIcon>
                <DevicesIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('profile.sessions.active')}
                secondary={t('profile.sessions.activeHelp')}
              />
            </ListItem>
          </List>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" color="warning" onClick={logout}>
              {t('profile.sessions.signOut')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <CardHeader
          title={t('profile.danger.title')}
          subheader={t('profile.danger.subtitle')}
          sx={{ color: 'error.main' }}
        />
        <Divider />
        <CardContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('profile.danger.warning')}
          </Alert>
          <List>
            <ListItem
              sx={{ pr: 22 }}
              secondaryAction={
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDeleteAccount}
                  startIcon={<DeleteForeverIcon />}
                >
                  {t('profile.danger.deleteAccount')}
                </Button>
              }
            >
              <ListItemIcon>
                <DeleteForeverIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t('profile.danger.deleteAccount')}
                secondary={t('profile.danger.deleteAccountHelp')}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Info Dialogs */}
      <Dialog open={dialogOpen === 'password'} onClose={closeDialog}>
        <DialogTitle>{t('profile.dialogs.changePassword')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('profile.dialogs.changePasswordBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common:ok')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen === 'sessions'} onClose={closeDialog}>
        <DialogTitle>{t('profile.dialogs.manageSessions')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('profile.dialogs.manageSessionsBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common:ok')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen === 'delete'} onClose={closeDialog}>
        <DialogTitle>{t('profile.dialogs.deleteAccount')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('profile.dialogs.deleteAccountBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t('common:cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
